/**
 * 貼文可見性設定的 form 內容（不含 Modal 殼，不含按鈕）。
 *
 * 由 PostVisibilityModal 與 PostEditModal sub-view 共用：
 * - PostVisibilityModal：MyPost / WorldCreate 使用，渲染為獨立 modal
 * - PostEditModal：在 modal 內以 sub-view 形式 render，配返回鍵切換
 *
 * 確認/取消按鈕統一放在呼叫端的 Modal.Footer（全站 modal 慣例），
 * 本元件只負責欄位，透過 ref 暴露 confirm()，並用 onValidityChange 回報目前是否可確認。
 */
import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { VISIBILITY_OPTIONS } from '@/shared/constants/postVisibility';

const PostVisibilityForm = forwardRef(({ initialValues = {}, onConfirm, onValidityChange }, ref) => {
  const { t } = useTranslation();
  const [visibilityType, setVisibilityType] = useState('0');
  const [unlockPrice, setUnlockPrice] = useState('');

  useEffect(() => {
    setVisibilityType(initialValues.visibilityType?.toString() || '0');
    setUnlockPrice(initialValues.unlockPrice?.toString() || '');
    // 依賴 initialValues 物件 ref 變化（呼叫端應在 open 時建立新 obj）
  }, [initialValues]);

  const handleVisibilityChange = (value) => {
    setVisibilityType(value);
    if (value !== '2' && value !== '4') {
      setUnlockPrice('');
    }
  };

  const needsUnlockPrice = visibilityType === '2' || visibilityType === '4';
  const isConfirmDisabled = needsUnlockPrice && (!unlockPrice || isNaN(unlockPrice) || Number(unlockPrice) <= 0);

  useEffect(() => {
    onValidityChange?.(!isConfirmDisabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfirmDisabled]);

  useImperativeHandle(ref, () => ({
    confirm: () => {
      if (isConfirmDisabled) return;
      onConfirm?.({
        visibilityType: parseInt(visibilityType),
        unlockPrice: needsUnlockPrice ? Number(unlockPrice) : null,
      });
    },
  }));

  return (
    <>
      <div className="mb-3">
        {/* 不再重複標題：modal 標題／sub-view 標題已經是「內容瀏覽權限」 */}
        {VISIBILITY_OPTIONS.map((option) => {
          // 選項值來自共用常數（number），radio 的 value 一律是 string，比對前先轉型
          const optionValue = String(option.value);
          const isSelected = visibilityType === optionValue;
          return (
            <label
              key={optionValue}
              htmlFor={`visibility-${optionValue}`}
              className={`visibility-option-card d-block rounded-3 border py-2 px-3 mb-2 ${isSelected ? 'border-primary bg-primary-subtle' : 'border-gray'}`}
            >
              <input
                type="radio"
                id={`visibility-${optionValue}`}
                name="visibility"
                value={optionValue}
                checked={isSelected}
                onChange={(e) => handleVisibilityChange(e.target.value)}
                className="visually-hidden"
              />
              <div className="d-flex align-items-center gap-3">
                <FontAwesomeIcon icon={option.icon} fixedWidth size="lg" className={isSelected ? 'text-primary' : 'text-gray'} />
                <div className="flex-grow-1 min-w-0">
                  <div className="fw-bold">{t(option.titleKey)}</div>
                  <small className="text-muted">{t(option.descKey)}</small>
                </div>
                {isSelected && <FontAwesomeIcon icon={faCheckCircle} className="text-primary flex-shrink-0" />}
              </div>
              {option.needsPrice && isSelected && (
                <div className="d-flex align-items-center gap-2 mt-2">
                  <Form.Label as="span" className="mb-0 text-nowrap text-primary">{t('unlock_price')}</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="FCoin"
                    value={unlockPrice}
                    onChange={(e) => setUnlockPrice(e.target.value)}
                    min="1"
                  />
                </div>
              )}
            </label>
          );
        })}
      </div>

      <div>
        <small className="text-gray">* {t('media_required_note')}</small>
      </div>
    </>
  );
});

PostVisibilityForm.displayName = 'PostVisibilityForm';

export default PostVisibilityForm;
